(() => {
  const containers = document.querySelectorAll("[data-client-product-order]");
  if (!containers.length) return;

  const PRODUCT_LIMIT = 250;

  const getPageFromUrl = () => {
    const page = Number(new URLSearchParams(window.location.search).get("page"));
    return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
  };

  const hasActiveFilters = () => {
    const params = new URLSearchParams(window.location.search);
    return Array.from(params.keys()).some((key) => key.startsWith("filter."));
  };

  const escapeHtml = (value) => {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const getVariants = (product) => {
    return Array.isArray(product.variants) ? product.variants : [];
  };

  const firstVariant = (product) => {
    return getVariants(product)[0] || null;
  };

  const isAvailable = (product) => {
    return getVariants(product).some((variant) => variant.available);
  };

  const getDateValue = (value) => {
    return new Date(value || 0).getTime() || 0;
  };

  const getImage = (product) => {
    return product.image || product.featured_image || (Array.isArray(product.images) ? product.images[0] : null);
  };

  const getImageSrc = (image) => {
    return image?.src || image?.url || "";
  };

  const getImageAlt = (image, product) => {
    return image?.alt || product.title || "";
  };

  const getRatioPercent = (image, imageRatio) => {
    if (imageRatio === "portrait") return 125;
    if (imageRatio === "square") return 100;

    const width = Number(image?.width);
    const height = Number(image?.height);
    const ratio = width > 0 && height > 0 ? width / height : Number(image?.aspect_ratio);

    return ratio > 0 ? 100 / ratio : 100;
  };

  const getPriceValue = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
  };

  const getMinPrice = (product) => {
    const prices = getVariants(product).map((variant) => getPriceValue(variant.price));
    return prices.length ? Math.min(...prices) : 0;
  };

  const hasSalePrice = (product) => {
    return getVariants(product).some((variant) => {
      const price = getPriceValue(variant.price);
      const compareAtPrice = getPriceValue(variant.compare_at_price);
      return compareAtPrice > price;
    });
  };

  const formatMoney = (amount, currency) => {
    if (currency === "JPY") {
      return `\u00a5${Math.round(amount).toLocaleString("ja-JP")} JPY`;
    }

    try {
      return `${new Intl.NumberFormat("ja-JP", {
        style: "currency",
        currency,
      }).format(amount)} ${currency}`;
    } catch (_error) {
      return `${Math.round(amount).toLocaleString("ja-JP")} ${currency}`;
    }
  };

  const productUrl = (product, collectionHandle) => {
    if (!product.handle) return "#";

    const productHandle = encodeURIComponent(product.handle);
    if (collectionHandle) {
      return `/collections/${encodeURIComponent(collectionHandle)}/products/${productHandle}`;
    }

    return `/products/${productHandle}`;
  };

  const buildBadge = (product, available) => {
    if (!available || !hasSalePrice(product)) return "";

    return '<span class="badge badge--bottom-left color-scheme-4">Sale</span>';
  };

  const buildProductCard = (product, index, options) => {
    const available = isAvailable(product);
    const image = getImage(product);
    const imageSrc = getImageSrc(image);
    const imageAlt = getImageAlt(image, product);
    const title = escapeHtml(product.title || "");
    const url = productUrl(product, options.collectionHandle);
    const price = formatMoney(getMinPrice(product), options.currency);
    const variant = firstVariant(product);
    const productId = product.id || variant?.id || index;
    const escapedId = escapeHtml(productId);
    const cardStyle = options.cardStyle || "standard";
    const cardColorScheme = options.cardColorScheme || "scheme-2";
    const ratioPercent = getRatioPercent(image, options.imageRatio);
    const animationClass = options.animationsEnabled ? " scroll-trigger animate--slide-in" : "";
    const animationAttrs = options.animationsEnabled ? ` data-cascade style="--animation-order: ${index};"` : "";
    const hasMedia = Boolean(imageSrc);
    const badge = buildBadge(product, available);

    const cardClasses = [
      "card",
      `card--${cardStyle}`,
      hasMedia ? "card--media" : "card--text",
      cardStyle === "card" ? `color-${cardColorScheme} gradient` : "",
    ]
      .filter(Boolean)
      .join(" ");
    const innerClasses = [
      "card__inner",
      cardStyle === "standard" ? `color-${cardColorScheme} gradient` : "",
      hasMedia || cardStyle === "standard" ? "ratio" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return `
      <li class="grid__item${animationClass}"${animationAttrs}>
        <div class="card-wrapper product-card-wrapper underline-links-hover${available ? "" : " card-wrapper--sold-out"}">
          <div class="${cardClasses}" style="--ratio-percent: ${ratioPercent}%;">
            <div class="${innerClasses}" style="--ratio-percent: ${ratioPercent}%;">
              ${
                hasMedia
                  ? `<div class="card__media">
                      <div class="media media--transparent media--hover-effect">
                        <img
                          src="${escapeHtml(imageSrc)}"
                          alt="${escapeHtml(imageAlt)}"
                          class="motion-reduce"
                          ${index > 2 ? 'loading="lazy"' : ""}
                          ${image?.width ? `width="${escapeHtml(image.width)}"` : ""}
                          ${image?.height ? `height="${escapeHtml(image.height)}"` : ""}
                        >
                      </div>
                      ${available ? "" : '<span class="card__sold-out-badge">SOLD OUT</span>'}
                    </div>`
                  : ""
              }
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading">
                    <a
                      href="${escapeHtml(url)}"
                      id="StandardCardNoMediaLink-${escapeHtml(options.sectionId)}-${escapedId}"
                      class="full-unstyled-link"
                      aria-labelledby="StandardCardNoMediaLink-${escapeHtml(options.sectionId)}-${escapedId} NoMediaStandardBadge-${escapeHtml(options.sectionId)}-${escapedId}"
                    >
                      ${title}
                    </a>
                  </h3>
                </div>
                <div class="card__badge bottom left">${badge}</div>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading${hasMedia || cardStyle === "standard" ? " h5" : ""}" id="title-${escapeHtml(options.sectionId)}-${escapedId}">
                  <a
                    href="${escapeHtml(url)}"
                    id="CardLink-${escapeHtml(options.sectionId)}-${escapedId}"
                    class="full-unstyled-link"
                    aria-labelledby="CardLink-${escapeHtml(options.sectionId)}-${escapedId} Badge-${escapeHtml(options.sectionId)}-${escapedId}"
                  >
                    ${title}
                  </a>
                </h3>
                <div class="card-information">
                  <div class="price${available ? "" : " price--sold-out"}">
                    <div class="price__container">
                      <div class="price__regular">
                        <span class="visually-hidden visually-hidden--inline">Regular price</span>
                        <span class="price-item price-item--regular">${escapeHtml(price)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card__badge bottom left">${badge}</div>
            </div>
          </div>
        </div>
      </li>
    `;
  };

  const pageUrl = (page) => {
    const url = new URL(window.location.href);
    if (page <= 1) {
      url.searchParams.delete("page");
    } else {
      url.searchParams.set("page", String(page));
    }
    return `${url.pathname}${url.search}${url.hash}`;
  };

  const buildPagination = (currentPage, totalPages) => {
    if (totalPages <= 1) return "";

    const links = [];
    if (currentPage > 1) {
      links.push(`
        <li>
          <a href="${escapeHtml(pageUrl(currentPage - 1))}" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" aria-label="Previous page">&lsaquo;</a>
        </li>
      `);
    }

    for (let page = 1; page <= totalPages; page += 1) {
      if (page === currentPage) {
        links.push(`
          <li>
            <a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page" aria-label="Page ${page}">${page}</a>
          </li>
        `);
      } else {
        links.push(`
          <li>
            <a href="${escapeHtml(pageUrl(page))}" class="pagination__item link" aria-label="Page ${page}">${page}</a>
          </li>
        `);
      }
    }

    if (currentPage < totalPages) {
      links.push(`
        <li>
          <a href="${escapeHtml(pageUrl(currentPage + 1))}" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" aria-label="Next page">&rsaquo;</a>
        </li>
      `);
    }

    return `
      <div class="pagination-wrapper" data-client-pagination data-page="${currentPage}">
        <nav class="pagination" role="navigation" aria-label="Pagination">
          <ul class="pagination__list list-unstyled" role="list">${links.join("")}</ul>
        </nav>
      </div>
    `;
  };

  const fetchCollectionProducts = async (handle) => {
    const products = [];

    for (let page = 1; page < 100; page += 1) {
      const response = await fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=${PRODUCT_LIMIT}&page=${page}`, {
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error(`Failed to fetch products: ${response.status}`);

      const data = await response.json();
      const pageProducts = Array.isArray(data.products) ? data.products : [];
      products.push(...pageProducts);

      if (pageProducts.length < PRODUCT_LIMIT) break;
    }

    return products;
  };

  const sortProducts = (products) => {
    return products.slice().sort((a, b) => {
      const availableDelta = Number(isAvailable(b)) - Number(isAvailable(a));
      if (availableDelta !== 0) return availableDelta;

      const publishedDelta = getDateValue(b.published_at) - getDateValue(a.published_at);
      if (publishedDelta !== 0) return publishedDelta;

      const createdDelta = getDateValue(b.created_at) - getDateValue(a.created_at);
      if (createdDelta !== 0) return createdDelta;

      return (Number(b.id) || 0) - (Number(a.id) || 0);
    });
  };

  const initContainer = async (container) => {
    if (hasActiveFilters()) return;

    const grid = container.querySelector("#product-grid");
    if (!grid) return;

    const collectionHandle = container.dataset.collectionHandle;
    if (!collectionHandle) return;

    const perPage = Number(container.dataset.productsPerPage) || 16;
    const options = {
      collectionHandle,
      sectionId: container.dataset.sectionId || "collection",
      currency: container.dataset.currency || "JPY",
      imageRatio: container.dataset.imageRatio || "adapt",
      cardStyle: container.dataset.cardStyle || "standard",
      cardColorScheme: container.dataset.cardColorScheme || "scheme-2",
      animationsEnabled: container.classList.contains("scroll-trigger"),
    };

    try {
      const sortedProducts = sortProducts(await fetchCollectionProducts(collectionHandle));
      if (!sortedProducts.length) return;

      const totalPages = Math.max(1, Math.ceil(sortedProducts.length / perPage));
      const currentPage = Math.min(getPageFromUrl(), totalPages);
      const start = (currentPage - 1) * perPage;
      const pageProducts = sortedProducts.slice(start, start + perPage);

      grid.innerHTML = pageProducts
        .map((product, index) => buildProductCard(product, index + 1, options))
        .join("");

      const paginationHtml = buildPagination(currentPage, totalPages);
      const existingPagination = container.querySelector(".pagination-wrapper");
      if (existingPagination) {
        existingPagination.outerHTML = paginationHtml;
      } else if (paginationHtml) {
        grid.insertAdjacentHTML("afterend", paginationHtml);
      }
    } catch (_error) {
      // Keep the Liquid-rendered product grid as the fallback.
    }
  };

  containers.forEach((container) => {
    initContainer(container);
  });
})();
